import React from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { toast } from 'react-toastify';

interface ContactFormData {
  nome: string;
  email: string;
  telefone: string;
  assunto: string;
  mensagem: string;
}

const schema = yup.object({
  nome: yup.string().required('Nome é obrigatório'),
  email: yup.string().email('Email inválido').required('Email é obrigatório'),
  telefone: yup.string().required('Telefone é obrigatório'),
  assunto: yup.string().required('Assunto é obrigatório'),
  mensagem: yup.string().required('Mensagem é obrigatória'),
});

const Contact: React.FC = () => {
  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<ContactFormData>({
    resolver: yupResolver(schema),
  });

  const onSubmit = async (data: ContactFormData) => {
    try {
      // Concatenar todas as informações do usuário
      const conteudoEmail = `
        <h2>Nova Solicitação de Proposta - Site FalifeTec</h2>
        <p><strong>Nome:</strong> ${data.nome}</p>
        <p><strong>Email:</strong> ${data.email}</p>
        <p><strong>Telefone:</strong> ${data.telefone}</p>
        <p><strong>Assunto:</strong> ${data.assunto}</p>
        <p><strong>Mensagem:</strong></p>
        <p>${data.mensagem.replace(/\n/g, '<br>')}</p>
        <hr>
        <p><small>Mensagem enviada através do formulário de contato do site.</small></p>
      `;

      // Enviar para a API
      const response = await fetch('https://webhook.cassiolima.dev.br/webhook/enviar-email', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Basic dGVzdGU6VGVzdGVARmFzdFNlcnZpY2U='
        },
        body: JSON.stringify({
          host: "email-ssl.com.br",
          porta: 465,
          usuario: "contato@falife.com.br",
          senha: "@Contato.10",
          destinatario: "lima164@gmail.com,lima08larissa@gmail.com",
          assunto: `Nova solicitação de proposta: ${data.assunto}`,
          conteudo: conteudoEmail
        })
      });

      if (response.ok) {
        toast.success('Mensagem enviada com sucesso! Entraremos em contato em breve.');
        reset();
      } else {
        throw new Error('Erro na resposta do servidor');
      }
    } catch (error) {
      console.error('Erro ao enviar email:', error);
      toast.error('Erro ao enviar mensagem. Tente novamente.');
    }
  };

  return (
    <section id="contact" className="section-padding bg-gray-50">
      <div ref={ref} className="container-custom">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          {/* Form */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: -50 }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-secondary-900 mb-6">
              Solicite uma proposta
            </h2>
            
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <input
                    {...register('nome')}
                    type="text"
                    placeholder="Nome"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
                      errors.nome ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.nome && (
                    <p className="mt-1 text-sm text-red-600">{errors.nome.message}</p>
                  )}
                </div>
                
                <div>
                  <input
                    {...register('email')}
                    type="email"
                    placeholder="Email"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
                      errors.email ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">{errors.email.message}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <input
                    {...register('telefone')}
                    type="tel"
                    placeholder="Telefone"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
                      errors.telefone ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.telefone && (
                    <p className="mt-1 text-sm text-red-600">{errors.telefone.message}</p>
                  )}
                </div>
                
                <div className="md:col-span-2">
                  <input
                    {...register('assunto')}
                    type="text"
                    placeholder="Assunto"
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${
                      errors.assunto ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.assunto && (
                    <p className="mt-1 text-sm text-red-600">{errors.assunto.message}</p>
                  )}
                </div>
              </div>

              <div>
                <textarea
                  {...register('mensagem')}
                  rows={4}
                  placeholder="Mensagem"
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors resize-none ${
                    errors.mensagem ? 'border-red-500' : 'border-gray-300'
                  }`}
                />
                {errors.mensagem && (
                  <p className="mt-1 text-sm text-red-600">{errors.mensagem.message}</p>
                )}
              </div>

              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-primary py-4 text-lg font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Enviando...' : 'Enviar'}
              </motion.button>
            </form>
          </motion.div>

          {/* Hero Message */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={inView ? { opacity: 1, x: 0 } : { opacity: 0, x: 50 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-center lg:text-left"
          >
            <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold font-heading text-gradient mb-6">
              Reinvente seu negócio!
            </h1>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
